/**
 * A picked file as a base64 `data:` URL, for a request that carries the
 * file in its body: the VIN reader, and an Urgently inspection's photos,
 * which the server decodes. Not `URL.createObjectURL`: a `blob:` URL means
 * something only inside this page, and the old inspection screen sent
 * those, so its photos never reached Urgently.
 */
export const toDataUrl = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/** The longest side a photo sent in a request body is scaled down to. */
export const PHOTO_MAX_EDGE = 1600;

/**
 * A photo as a JPEG `data:` URL no wider or taller than `maxEdge`. A phone
 * photo is 3 to 5 MB, a third more as base64, and the server takes 10 MB
 * of JSON, so three of them at full size were refused. At 1600px a photo
 * is a few hundred KB and still shows the damage. Where the browser cannot
 * decode it here (no createImageBitmap), it goes as it is.
 */
export async function toPhotoDataUrl(
  file: Blob,
  maxEdge = PHOTO_MAX_EDGE
): Promise<string> {
  if (typeof createImageBitmap !== 'function') return toDataUrl(file);
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return toDataUrl(file);
  }
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    return toDataUrl(file);
  }
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas.toDataURL('image/jpeg', 0.8);
}
